import { LOCALES, DEFAULT_LOCALE, type Locale } from '@tg/shared';

export { LOCALES, DEFAULT_LOCALE, type Locale };

export function isLocale(x: unknown): x is Locale {
  return typeof x === 'string' && (LOCALES as readonly string[]).includes(x);
}

/** BCP-47 tags for Intl (numbers, dates, plurals). */
export const INTL_TAG: Record<Locale, string> = {
  en: 'en-US',
  de: 'de-DE',
  zh: 'zh-CN',
  ru: 'ru-RU',
};

/** Self-names: never translated (a German speaker looks for "Deutsch" in any UI language). */
export const LOCALE_SHORT: Record<Locale, string> = { en: 'EN', de: 'DE', zh: '中文', ru: 'RU' };
export const LOCALE_NAME: Record<Locale, string> = {
  en: 'English',
  de: 'Deutsch',
  zh: '中文',
  ru: 'Русский',
};

/** Pick the first supported language from navigator.languages (e.g. "de-AT" → de, "zh-Hans-CN" → zh). */
export function detectLocale(languages: readonly string[] | undefined): Locale {
  for (const tag of languages ?? []) {
    const base = tag.toLowerCase().split(/[-_]/)[0];
    if (isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}

export const LOCALE_STORAGE_KEY = 'tg.locale';

export function readStoredLocale(): Locale | null {
  try {
    const v = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(v) ? v : null;
  } catch {
    return null;
  }
}

export function storeLocale(l: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, l);
  } catch {
    /* private mode — ignore */
  }
}

/** Replace the leading /:lang segment of a pathname ("/de/pot" → "/ru/pot"). */
export function swapLocaleInPath(pathname: string, to: Locale): string {
  const parts = pathname.split('/');
  if (parts.length > 1 && parts[1]) {
    parts[1] = to;
    return parts.join('/');
  }
  return `/${to}`;
}
